import React, { memo } from "react";

import { ShowcaseCard, ShowcaseIntro, ShowcaseNotice } from "ui/components/Showcase";

import {
  FORGOT_PASSWORD_INTRO_EMAIL,
  FORGOT_PASSWORD_INTRO_MASK_LENGTH,
  FORGOT_PASSWORD_INTRO_TEXT,
} from "./consts";

/**
 * Lijevi plavi panel zaboravljene lozinke: email s poveznicom za promjenu, kartica zaštićene
 * nove lozinke i obavijest o poslanoj poveznici. Vizualno prati panel prijave.
 */
const ForgotPasswordIntro: React.FC = () => {
  return (
    <div className="hidden lg:flex w-1/2 h-screen">
      <ShowcaseIntro
        sceneKey="forgot-password"
        title={FORGOT_PASSWORD_INTRO_TEXT.title}
        description={FORGOT_PASSWORD_INTRO_TEXT.description}
      >
        <ShowcaseCard className="left-[0.5rem] top-0 w-[17rem]" delay={0}>
          <div className="h-2 rounded-t-lg bg-gradient-to-r from-blue-500 to-sky-400" />
          <div className="p-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 flex-shrink-0">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
              </span>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-700">{FORGOT_PASSWORD_INTRO_EMAIL.sender}</div>
                <div className="text-[0.65rem] text-slate-400 truncate">za {FORGOT_PASSWORD_INTRO_EMAIL.recipient}</div>
              </div>
            </div>

            <div className="mt-4 text-sm font-bold text-slate-800">{FORGOT_PASSWORD_INTRO_EMAIL.subject}</div>
            <div className="mt-2 space-y-1.5">
              <div className="h-1.5 w-full rounded-full bg-slate-100" />
              <div className="h-1.5 w-5/6 rounded-full bg-slate-100" />
              <div className="h-1.5 w-3/6 rounded-full bg-slate-100" />
            </div>

            <div className="mt-4 rounded-md bg-blue-700 py-2 text-center text-[0.7rem] font-semibold text-white">
              {FORGOT_PASSWORD_INTRO_EMAIL.action}
            </div>
          </div>
        </ShowcaseCard>

        <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
          <div className="p-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex-shrink-0">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </span>
              <div>
                <div className="text-xs font-bold text-slate-800">Nova lozinka</div>
                <div className="text-[0.65rem] text-slate-400">Zaštićeno</div>
              </div>
            </div>

            {/* Skriveno polje lozinke: točkice umjesto znakova. */}
            <div className="mt-4 flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2.5">
              {Array.from({ length: FORGOT_PASSWORD_INTRO_MASK_LENGTH }, (_, index) => (
                <span key={index} className="w-1.5 h-1.5 rounded-full bg-slate-700" />
              ))}
            </div>
            <div className="mt-3 flex gap-1">
              <div className="h-1 flex-1 rounded-full bg-emerald-500" />
              <div className="h-1 flex-1 rounded-full bg-emerald-500" />
              <div className="h-1 flex-1 rounded-full bg-emerald-500" />
              <div className="h-1 flex-1 rounded-full bg-slate-100" />
            </div>
            <div className="mt-1.5 text-[0.65rem] font-semibold text-emerald-600">Jaka lozinka</div>
          </div>
        </ShowcaseCard>

        <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
          <ShowcaseNotice
            title="Poveznica je poslana"
            subtitle="Provjerite ulaznu poštu"
            iconClassName="bg-amber-100"
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            }
          />
        </ShowcaseCard>
      </ShowcaseIntro>
    </div>
  );
};

export default memo(ForgotPasswordIntro);
